import { ExperimentEditDrawer } from 'modules/experiments/components/pages';
import 'modules/experiments/takeover/ducks/sagas';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RUM_PURPOSE, addTeamContextToRUM } from 'services/observability/datadog';
import { useRumAction, useRumContext } from 'services/observability/datadog/RumComponentContext';
import { Product, Team } from 'teams';
import { ExperimentTakeoverSource } from '../constants';
import { updateExperimentLocale } from '../ducks/actions';
import { getTakeoverExperimentLocale } from '../ducks/selectors';
import { usePrivateExperimentTakeover } from '../hooks';
import { CreateExperimentFormProvider, ExperimentCreationModal } from './ExperimentCreationModal';

type ExperimentTakeoverProps = {
  /** ExperimentTakeoverSource to identify the entry point to the experiment takeover */
  source: ExperimentTakeoverSource;
  currentLocale?: string;
  setCurrentLocale: (locale: string) => void;
  /** Callback function on takeover close. Normally used to refetch data on the base page */
  onClose: () => void;
};

export const ExperimentTakeover = ({
  source,
  currentLocale,
  setCurrentLocale,
  onClose,
}: ExperimentTakeoverProps) => {
  const dispatch = useDispatch();
  const { isExperimentCreationModalOpen, isExperimentTakeoverOpen, setExperimentTakeoverSource } =
    usePrivateExperimentTakeover();
  const { teamName, productName, setTeamName, setProductName } = useRumContext();
  const addSignal = useRumAction(RUM_PURPOSE.Takeover);
  const locale = useSelector(getTakeoverExperimentLocale);
  const [telemetrySourceUpdated, setTelemetrySourceUpdated] = useState(false);
  const [original] = useState({ teamName, productName });

  /**
   * Sync up the locale selected in the
   * base page to our redux store
   * */
  useEffect(() => {
    dispatch(updateExperimentLocale(currentLocale));
  }, [currentLocale]);

  /**
   * Sync up the locale saved in the redux store
   * back to the context of the base page
   * */
  useEffect(() => {
    if (!isExperimentTakeoverOpen) return;
    setCurrentLocale(locale);
  }, [isExperimentTakeoverOpen, locale]);

  /** Set the source of the takeover on load */
  useEffect(() => {
    setExperimentTakeoverSource(source);
  }, []);

  /**
   * We update the rum context to Experiments when the modal or takeover is open.
   * So, that we can track any errors occurring inside them under the Experiments team
   */
  useEffect(() => {
    const isExperimentModuleOpen = isExperimentCreationModalOpen || isExperimentTakeoverOpen;
    const currentTeamName = isExperimentModuleOpen ? Team.EXPERIMENT_TEAM : original.teamName;
    const currentProductName = isExperimentModuleOpen
      ? Product.EXPERIMENT_EDITOR
      : original.productName;

    setTeamName(currentTeamName);
    setProductName(currentProductName);
    addTeamContextToRUM({ name: currentTeamName, product: currentProductName });

    if (isExperimentCreationModalOpen) {
      addSignal('EXPERIMENT_CREATION_MODAL_OPENED');
    }
    if (isExperimentTakeoverOpen) {
      addSignal('EXPERIMENT_EDITOR_OPENED');
    }
  }, [isExperimentCreationModalOpen, isExperimentTakeoverOpen]);

  /**
   * Verify that the telemetry source has been updated first
   * Before we render the modal and takeover.
   */
  useEffect(() => {
    if (teamName === Team.EXPERIMENT_TEAM && productName === Product.EXPERIMENT_EDITOR) {
      setTelemetrySourceUpdated(true);
    } else {
      setTelemetrySourceUpdated(false);
    }
  }, [teamName, productName]);

  return telemetrySourceUpdated ? (
    <>
      {isExperimentCreationModalOpen && (
        <CreateExperimentFormProvider>
          <ExperimentCreationModal />
        </CreateExperimentFormProvider>
      )}
      {isExperimentTakeoverOpen && <ExperimentEditDrawer onClose={onClose} />}
    </>
  ) : null;
};
